import { useEffect, useState, useCallback } from "react";
import { usePlaidLink } from "react-plaid-link";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import api from "../api/api";
import {
  QUERY_PLAID_LINK_TOKEN,
  QUERY_EXTERNAL_ACCOUNTS,
} from "../api/queryKeys";
import { PLAID_LINK_TOKEN } from "../helpers/localStorage";
import Button from "./Button";
import Loader from "./Loader";

const PlaidConnection = ({
  buttonWidth = "242px",
}: {
  buttonWidth?: string;
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  let isOauth = false;

  const generateToken = async () => {
    // For OAuth, use previously generated Link token
    if (window.location.href.includes("?oauth_state_id=")) {
      const storedToken = localStorage.getItem(PLAID_LINK_TOKEN);
      if (storedToken) {
        setLinkToken(storedToken);
      }
    } else {
      try {
        const response = await api.post(QUERY_PLAID_LINK_TOKEN, {
          platform_os: "web",
          redirect_url: window.location.href,
        });
        const linkToken = response.data.link_token;
        localStorage.setItem(PLAID_LINK_TOKEN, linkToken);
        setLinkToken(linkToken);
        return linkToken;
      } catch (error) {
        toast.error("Error connecting to Plaid", {
          toastId: "plaid-token-error",
        });
      }
    }
  };

  const onSuccess = useCallback(
    async (public_token: string, metadata: any) => {
      setIsLoading(true);
      const account = metadata.accounts[0];
      const accountId = account?.id;

      // send public_token to server
      try {
        await api.post(QUERY_EXTERNAL_ACCOUNTS, {
          plaid_public_token: public_token,
          plaid_selected_account_id: accountId,
        });
        // Invalide query to refetch external accounts
        queryClient.invalidateQueries([QUERY_EXTERNAL_ACCOUNTS]);
        setIsLoading(false);
      } catch {
        toast.error("Error connecting external account");
        setIsLoading(false);
      }
    },
    [queryClient]
  );

  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken,
    onSuccess,
    onEvent: (eventName, metadata) => {
      if (
        eventName === "EXIT" &&
        metadata.error_code === "OAUTH_STATE_ID_ALREADY_PROCESSED"
      ) {
        localStorage.removeItem(PLAID_LINK_TOKEN);
        toast.error(
          "This account has already been connected. Please try again."
        );
      }
    },
  };

  // For OAuth, configure the received redirect URI
  if (window.location.href.includes("?oauth_state_id=")) {
    config.receivedRedirectUri = window.location.href;
    isOauth = true;
  }

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (!linkToken) {
      generateToken();
    }
    if (isOauth && ready) {
      open();
    }
  }, [linkToken, isOauth, ready, open]);

  if (isLoading) {
    return <Loader text="Connecting account..." />;
  }

  return (
    <Button
      btntype="secondary"
      width={buttonWidth}
      onClick={() => open()}
      disabled={!ready}
    >
      Connect External Account
    </Button>
  );
};

export default PlaidConnection;
